// src/screens/cadastro/Register.js

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc, // MUDANÇA: Usamos getDoc para leitura direta
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../../firebaseconfig";
import { Eye, EyeOff } from "lucide-react";
import "./Register.css";

// --- FUNÇÕES AUXILIARES ---

const formatarCelular = (value) => {
  const v = value.replace(/\D/g, "").slice(0, 11);
  if (v.length <= 2) return v;
  if (v.length <= 7) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
};

function errosFirebase(error) {
  console.warn("Código de erro Firebase:", error.code);
  switch (error.code) {
    case "auth/email-already-in-use":
      return "Este e-mail já está sendo usado.";
    case "auth/invalid-email":
      return "E-mail inválido.";
    case "auth/weak-password":
      return "A senha deve ter pelo menos 6 caracteres.";
    case "auth/network-request-failed":
      return "Sem conexão com a internet.";
    default:
      return `Erro ao cadastrar: ${error.message}`;
  }
}

// 1. Verifica na coleção PÚBLICA (cpfs_vinculados)
// MUDANÇA: Busca direta pelo ID (que é o CPF). Muito mais rápido e barato.
async function verificarCpfJaCadastrado(cpfNumerico) {
  try {
    const docRef = doc(db, "cpfs_vinculados", cpfNumerico);
    const docSnap = await getDoc(docRef);

    // Se o documento existe, o CPF já está cadastrado
    return docSnap.exists();
  } catch (error) {
    console.error("Erro ao verificar CPF vinculado:", error);
    throw new Error("Erro de conexão ao validar disponibilidade do CPF.");
  }
}

async function salvarDados(authObj, cpf, nomeCompleto, celular) {
  try {
    const uid = authObj.user.uid;
    const email = authObj.user.email;

    // A. Salva o Perfil do Usuário (Coleção 'usuarios')
    // ID = UID do Auth
    const usuarioRef = doc(db, "usuarios", uid);
    await setDoc(
      usuarioRef,
      {
        uid: uid,
        email: email,
        cpf: cpf,
        nomeCompleto: nomeCompleto,
        celular: celular,
        premium: false,
        customerEmail: email,
        calcleite: false,
        calccafe: false,
        calcbovCria: false,
        calcbovRecria: false,
        calcbovEngorda: false,
        calcagro: false,
        dataCadastro: serverTimestamp(),
        ultimaAtualizacao: serverTimestamp(),
        emailConfirmado: true,
      },
      { merge: true },
    );

    // B. Salva o Vínculo do CPF (Coleção 'cpfs_vinculados')
    // MUDANÇA: O ID do documento é o PRÓPRIO CPF (ex: 01253329478)
    const cpfVinculoRef = doc(db, "cpfs_vinculados", cpf);
    await setDoc(cpfVinculoRef, {
      uid: uid, // Salva o UID dentro para referência
      criadoEm: serverTimestamp(),
    });
  } catch (e) {
    console.error("Erro ao salvar dados:", e);
    throw new Error("Falha ao salvar os dados no banco.");
  }
}

// Fluxo principal de cadastro
async function cadastrar(email, senha, cpf, nomeCompleto, celular) {
  try {
    const cpfNumerico = cpf.replace(/[^\d]/g, "");

    // 1. Verifica CPF na coleção específica (Antes de criar Auth)
    const cpfExiste = await verificarCpfJaCadastrado(cpfNumerico);

    if (cpfExiste) {
      return "Este CPF já está cadastrado em outra conta.";
    }

    // 2. Cria usuário no Auth
    const dadosDoUsuario = await createUserWithEmailAndPassword(
      auth,
      email,
      senha,
    );

    // 3. Salva dados em 'usuarios' E 'cpfs_vinculados'
    await salvarDados(dadosDoUsuario, cpfNumerico, nomeCompleto, celular);

    return "sucesso";
  } catch (error) {
    console.error("Erro no fluxo:", error);
    if (
      error.message === "Falha ao salvar os dados no banco." ||
      error.message === "Erro de conexão ao validar disponibilidade do CPF."
    ) {
      return error.message;
    }
    return errosFirebase(error);
  }
}

const Register = () => {
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [email, setEmail] = useState("");
  const [cpf, setCpf] = useState("");
  const [celular, setCelular] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Validadores
  const validarCPF = (cpf) => {
    cpf = cpf.replace(/[^\d]/g, "");
    if (cpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpf)) return false;
    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i)) * (10 - i);
    let resto = 11 - (soma % 11);
    let digito1 = resto === 10 || resto === 11 ? 0 : resto;
    if (digito1 !== parseInt(cpf.charAt(9))) return false;
    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i)) * (11 - i);
    resto = 11 - (soma % 11);
    let digito2 = resto === 10 || resto === 11 ? 0 : resto;
    return digito2 === parseInt(cpf.charAt(10));
  };

  const formatarCPF = (text) => {
    const v = text.replace(/[^\d]/g, "").slice(0, 11);
    if (v.length <= 3) return v;
    if (v.length <= 6) return `${v.slice(0, 3)}.${v.slice(3)}`;
    if (v.length <= 9) return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
    return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9, 11)}`;
  };

  const handleCpfChange = (e) => setCpf(formatarCPF(e.target.value));
  const handleCelularChange = (e) =>
    setCelular(formatarCelular(e.target.value));

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);

    if (!agreedToTerms)
      return setError("Você deve concordar com a Política de Privacidade.");
    if (!nomeCompleto.trim()) return setError("O nome completo é obrigatório.");
    if (!validarCPF(cpf)) return setError("CPF inválido.");
    if (celular.replace(/\D/g, "").length < 10)
      return setError("Celular inválido.");
    if (password.length < 6)
      return setError("A senha deve ter no mínimo 6 caracteres.");
    if (password !== confirmPassword)
      return setError("A confirmação de senha não confere.");

    setIsLoading(true);

    try {
      const resultado = await cadastrar(
        email.trim(),
        password,
        cpf,
        nomeCompleto.trim(),
        celular,
      );

      if (resultado === "sucesso") {
        navigate("/assinar");
      } else {
        setError(resultado);
        setIsLoading(false);
      }
    } catch (err) {
      setError("Ocorreu um erro inesperado. Tente novamente.");
      setIsLoading(false);
    }
  };

  return (
    <div className="agroauth-register-container">
      <div className="agroauth-register-card">
        <div className="agroauth-register-header">
          <h2 className="agroauth-register-title">Crie sua conta</h2>
          <p className="agroauth-register-subtitle">Preencha os dados abaixo</p>
        </div>

        <form onSubmit={handleRegister} className="agroauth-register-form">
          {error && <div className="agroauth-error-message">{error}</div>}

          <div className="agroauth-input-group">
            <label className="agroauth-input-label">Nome Completo</label>
            <input
              type="text"
              value={nomeCompleto}
              onChange={(e) => setNomeCompleto(e.target.value)}
              className="agroauth-input-field"
              placeholder="Seu nome"
              required
            />
          </div>

          <div className="agroauth-input-group">
            <label className="agroauth-input-label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="agroauth-input-field"
              placeholder="seu@email.com"
              required
            />
          </div>

          <div className="agroauth-input-group">
            <label className="agroauth-input-label">CPF</label>
            <input
              type="text"
              value={cpf}
              onChange={handleCpfChange}
              className="agroauth-input-field"
              placeholder="000.000.000-00"
              maxLength={14}
              required
            />
          </div>

          <div className="agroauth-input-group">
            <label className="agroauth-input-label">Celular</label>
            <input
              type="text"
              value={celular}
              onChange={handleCelularChange}
              className="agroauth-input-field"
              placeholder="(00) 00000-0000"
              maxLength={15}
              required
            />
          </div>

          <div className="agroauth-input-group">
            <label className="agroauth-input-label">Senha</label>
            <div className="agroauth-password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="agroauth-input-field"
                placeholder="Mínimo 6 caracteres"
                required
              />
              <button
                type="button"
                className="agroauth-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff size={20} color="#718096" />
                ) : (
                  <Eye size={20} color="#718096" />
                )}
              </button>
            </div>
          </div>

          <div className="agroauth-input-group">
            <label className="agroauth-input-label">Confirmar Senha</label>
            <div className="agroauth-password-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="agroauth-input-field"
                placeholder="Repita a senha"
                required
              />
              <button
                type="button"
                className="agroauth-password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff size={20} color="#718096" />
                ) : (
                  <Eye size={20} color="#718096" />
                )}
              </button>
            </div>
          </div>

          <div className="agroauth-terms-group">
            <input
              id="agroauth-terms"
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="agroauth-terms-checkbox"
            />
            <label htmlFor="agroauth-terms" className="agroauth-terms-label">
              Li e concordo com a{" "}
              <a
                href="/politica-de-privacidade"
                target="_blank"
                rel="noopener noreferrer"
              >
                Política de Privacidade
              </a>
              .
            </label>
          </div>

          <button
            type="submit"
            className="agroauth-register-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="agroauth-spinner"></span>
            ) : (
              "Criar minha conta"
            )}
          </button>
        </form>

        <div className="agroauth-register-footer">
          <p className="agroauth-login-prompt-text">
            Já tem conta?{" "}
            <Link to="/login" className="agroauth-login-prompt-link">
              Acesse aqui
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
